import { useMemo } from 'react';
import { Alert, Box, Text, Title } from '@nimbus-ds/components';
import { Trans, useTranslation } from 'react-i18next';
import { CurrencyType } from '@tiendanube/common';
import { InterfaceText } from '@tiendanube/components';
import { CurrencyPrice } from 'commons/components';
import { formatCurrency } from 'commons/utils';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

interface SummaryDifferenceProps {
  totalPaid: number;
  newTotal: number;
  currency: CurrencyType;
  isInsufficientStock?: boolean;
  fulfillmentWithoutProducts?: string;
  failedFulfillmentCotization?: string;
}

function SummaryMessage({
  totalPaid,
  newTotal,
  currency,
  fulfillmentWithoutProducts,
  failedFulfillmentCotization,
  isInsufficientStock,
}: Readonly<SummaryDifferenceProps>) {
  const t = useTranslationOrders();
  const {
    i18n: { language },
  } = useTranslation();

  const totalDifference = Math.abs(newTotal - totalPaid);

  const showAlertText = useMemo(
    () =>
      fulfillmentWithoutProducts !== undefined ||
      failedFulfillmentCotization !== undefined ||
      (newTotal === totalPaid && totalPaid !== 0),
    [
      fulfillmentWithoutProducts,
      newTotal,
      totalPaid,
      failedFulfillmentCotization,
    ],
  );

  const showTitle = useMemo(
    () => fulfillmentWithoutProducts === undefined,
    [fulfillmentWithoutProducts],
  );

  const getSummaryBackgroundColor = () => {
    if (isInsufficientStock) return 'danger-surface';
    if (fulfillmentWithoutProducts !== undefined) return 'danger-surface';
    if (failedFulfillmentCotization !== undefined) return 'warning-surface';
    if (newTotal === totalPaid) return 'success-surface';
    return 'warning-surface';
  };

  const getTotalColor: () => InterfaceText['appearance'] = () => {
    if (isInsufficientStock) return 'default';
    if (newTotal === totalPaid) return 'success';
    return 'warning';
  };

  const getTextColor = () => {
    if (isInsufficientStock) return 'neutral-textLow';
    if (fulfillmentWithoutProducts !== undefined) return 'danger-textLow';
    if (failedFulfillmentCotization !== undefined) return 'warning-textLow';
    if (newTotal === totalPaid) return 'success-textLow';
    return 'warning-textLow';
  };

  const getAlertAppearance = () => {
    if (isInsufficientStock) return 'danger';
    if (fulfillmentWithoutProducts !== undefined) return 'danger';
    if (failedFulfillmentCotization !== undefined) return 'warning';
    if (newTotal === totalPaid) return 'success';
    return 'warning';
  };

  const alertTextKey = useMemo(() => {
    if (isInsufficientStock) return 'editOrders.summary.alert.missingStock';
    if (fulfillmentWithoutProducts === '')
      return 'editOrders.summary.alert.emptyFulfillment';
    if (fulfillmentWithoutProducts)
      return 'editOrders.summary.alert.emptyFulfillmentLocation';
    if (failedFulfillmentCotization === '')
      return 'editOrders.summary.alert.failedOrderCotization';
    if (failedFulfillmentCotization !== undefined)
      return 'editOrders.summary.alert.failedFulfillmentCotization';
    if (newTotal === totalPaid) return 'editOrders.summary.alert.newEqualTotal';
    return '';
  }, [
    isInsufficientStock,
    fulfillmentWithoutProducts,
    newTotal,
    totalPaid,
    failedFulfillmentCotization,
  ]);

  const titleKey = useMemo(() => {
    if (newTotal > totalPaid)
      return 'editOrders.fulfillmentsSummary.alert.higherTitle';
    if (totalPaid > newTotal)
      return 'editOrders.fulfillmentsSummary.alert.lowerTitle';
    return 'editOrders.fulfillmentsSummary.alert.equalTitle';
  }, [totalPaid, newTotal]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      borderRadius="2"
      padding="2"
      backgroundColor={getSummaryBackgroundColor()}
    >
      {showTitle && (
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box display="flex" justifyContent="center" alignItems="center">
            <Title
              fontWeight="bold"
              fontSize="4"
              lineHeight="5"
              color={getTextColor()}
            >
              {t(titleKey)}
            </Title>
          </Box>
          <Box flexDirection="row" display="flex" paddingLeft="2">
            <CurrencyPrice
              price={totalDifference}
              currency={currency}
              appearance={getTotalColor()}
              size="highlight"
              bold
            />
          </Box>
        </Box>
      )}
      {showAlertText && (
        <Box paddingTop={showTitle ? '1' : undefined}>
          <Alert appearance={getAlertAppearance()}>
            <Text color={getTextColor()}>
              <Trans
                t={t}
                i18nKey={alertTextKey}
                values={{
                  amount: formatCurrency(totalDifference, currency, language),
                  location: fulfillmentWithoutProducts,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </Text>
          </Alert>
        </Box>
      )}
    </Box>
  );
}

export default SummaryMessage;
